import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { RxUpdate } from 'react-icons/rx';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useRolePermissionContext } from '../../../contexts/RolePermissionProvider';
import useRolePermissionsActions from '../../../hooks/APIHooks/users/useRolePermissionsActions';
import useRolesActions from '../../../hooks/APIHooks/users/useRolesActions';
import { SelectData } from '../../../types/common';
import { Permissions } from '../../../types/permissions';
import { RolePermissionReqBody } from '../../../types/rolePermissions';
import { User } from '../../../types/users';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import RolePermissionTable from '../../SetupSubPageComps/RolePermissionsComps/RolePermissionTable';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const AddEditRolePermissionModal = ({
  title,
  handleModalClose,
}: {
  title: 'add' | 'update';
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [initialValues, setInitialValues] = useState<{ [key: string]: any }>({
    role: SELECT_FIELD_DEFAULT_VALUE,
  });
  const [selectedRole, setSelectedRole] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );

  // integration of context hooks here
  const { featureList, rolePermissionList } = useRolePermissionContext();
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { getRoleListHandler } = useRolesActions('roleList');
  const {
    getRolePermissionsByRoleIdHandler,
    createRolePermissionHandler,
    updateRolePermissionHandler,
  } = useRolePermissionsActions(
    '',
    selectedRole.id !== -1 ? selectedRole.id : undefined
  );

  console.log(
    'ROLE WISE PERMISSIONS: ',
    getRolePermissionsByRoleIdHandler.data?.data
  );

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // setting role list data based on modal type here
  const roleList = useMemo(() => {
    if (!getRoleListHandler.data?.is_success) return [];

    if (title === 'add') {
      return getRoleListHandler.data.data
        .filter(
          (role) =>
            !rolePermissionList.some(
              (permission) => permission.role_name === role.role_name
            )
        )
        .map((role) => ({
          id: role.id,
          name: role.role_name,
        }));
    } else {
      return rolePermissionList.map((permission) => ({
        id: permission.role,
        name: permission.role_name,
      }));
    }
  }, [getRoleListHandler.data, rolePermissionList, title]);

  // setting up initial values when feature list is fetched here
  useEffect(() => {
    if (featureList.length) {
      let permissionStates = {};

      if (title === 'add') {
        featureList.forEach((feature) => {
          permissionStates = {
            ...permissionStates,
            [`${feature.name}_create`]: false,
            [`${feature.name}_read`]: false,
            [`${feature.name}_update`]: false,
            [`${feature.name}_delete`]: false,
          };
        });
      } else {
        if (getRolePermissionsByRoleIdHandler.data?.is_success) {
          const roleWisePermissions =
            getRolePermissionsByRoleIdHandler.data.data.role_permissions;

          featureList.forEach((feature) => {
            const featurePermissions =
              roleWisePermissions.find(
                (permission) => permission.feature === feature.name
              )?.permission_names || [];

            permissionStates = {
              ...permissionStates,
              role: selectedRole,
              [`${feature.name}_create`]: featurePermissions.includes('CREATE'),
              [`${feature.name}_read`]: featurePermissions.includes('READ'),
              [`${feature.name}_update`]: featurePermissions.includes('UPDATE'),
              [`${feature.name}_delete`]: featurePermissions.includes('DELETE'),
            };
          });
        }
      }

      setInitialValues((state) => ({
        ...state,
        ...permissionStates,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureList, title, getRolePermissionsByRoleIdHandler.data]);

  console.log('INITIAL VALUES: ', initialValues);

  // handler function to handle add/edit role permission feature declared here
  const addEditRolePermissionHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);
      console.log('ROLE PERMISSION VALUES: ', values);

      const featurePermissions: Record<string, Array<Permissions>> = {};

      Object.entries(values).forEach(([key, value]) => {
        if (key !== 'role' && value) {
          const [featureName, permission] = key.split('_');

          if (!featurePermissions[featureName]) {
            featurePermissions[featureName] = [];
          }

          featurePermissions[featureName].push(
            permission.toUpperCase() as Permissions
          );
        }
      });

      const selectedRolePermissions = Object.entries(featurePermissions).map(
        ([feature, permission_names]) => ({
          feature,
          permission_names,
        })
      );

      let body: RolePermissionReqBody = {
        role: values.role.id,
        role_permissions: [
          ...selectedRolePermissions,
          ...featureList
            .filter(
              (feature) =>
                !selectedRolePermissions.some(
                  (permission) => permission.feature === feature.name
                )
            )
            .map((feature) => ({
              feature: feature.name,
              permission_names: [],
            })),
        ],
      };

      console.log('ROLE PERMISSION REQ BODY: ', body);

      if (title === 'add') {
        body = {
          ...body,
          created_by: currentUser?.username,
        };

        await createRolePermissionHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Role Permission Created!!');

              handleModalClose();

              queryClient.invalidateQueries({
                queryKey: ['role-permission-list'],
              });

              queryClient.invalidateQueries({
                queryKey: [
                  'role-permission-by-role-id',
                  (currentUser as User).role_id,
                ],
              });
            } else {
              toast.error(res.message || 'Permission Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body = {
          ...body,
          updated_by: currentUser?.username,
        };

        await updateRolePermissionHandler
          .mutateAsync({ roleId: selectedRole.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Role Permission Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: ['role-permission-list'],
              });

              queryClient.invalidateQueries({
                queryKey: [
                  'role-permission-by-role-id',
                  (currentUser as User).role_id,
                ],
              });
            } else {
              toast.error(res.message || 'Permission Update Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering add/edit role permission modal component here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[700px]'>
      <ModalTitle titleText={`${title} role permission`} />

      <Formik
        initialValues={initialValues}
        onSubmit={addEditRolePermissionHandler}
        enableReinitialize
      >
        {(props) => (
          <Form className='space-y-4'>
            <AcademiaFormSelect
              name='role'
              label='Select Role'
              options={roleList}
              defaultValue={selectedRole}
              setValueOnChange={(role) => setSelectedRole(role)}
              isRequired
            />

            {props.values.role.id !== -1 && (
              <RolePermissionTable featureList={featureList} />
            )}

            <AcademiaButton
              title={
                props.values.role.id === -1
                  ? 'Please, select a role first!'
                  : undefined
              }
              type='submit'
              variant='premium'
              isLoading={props.isSubmitting}
              isDisabled={props.values.role.id === -1}
              icon={
                title === 'add' ? (
                  <TbSquareRoundedPlusFilled className='size-6' />
                ) : (
                  <RxUpdate className='size-6' />
                )
              }
              fullWidth
            >
              {title} Role Permission
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default AddEditRolePermissionModal;
